import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { Tag } from "@/DesignSystem";
import SimpleTable from "@/Flowsheets/Components/SimpleTable/SimpleTable";

import styles from "./MedicationTable.module.scss";

/**
 * Interface representing the props of the MedicationTable
 */
interface MedicationTableProps {
    /**
     * List of medications to be displayed in the table
     */
    medications: MedicationTableItem[];
}

/**
 * Enum representing the status of a medication
 */
enum MedicationStatus {
    Active = "Active",
    Expired = "Expired",
    Suspended = "Suspended"
}

/**
 * Interface representing a medication list item
 */
interface MedicationTableItem {
    /**
     * Unique identifier for the medication
     */
    id: Guid;

    /**
     * Name of the medication
     */
    name: string;

    /**
     * Indicates whether the medication is currently active
     */
    status: MedicationStatus;

    /**
     * Start date of the medication
     */
    startDate: string;

    /**
     * End date of the medication
     */
    endDate: string;

    /**
     * Dosage of the medication
     */
    dosage: string;

    /**
     * Quantity of the medication prescribed
     */
    quantity: string;

    /**
     * Renewals available for the medication
     */
    renewals: string;

    /**
     * Duration for which the medication is prescribed
     */
    duration: string;
}

function NameCell(medication: Readonly<MedicationTableItem>): JSX.Element {
    const { getText } = useTranslation();

    return (
        <div className={styles.medicationNameCell}>
            {medication.name}
            {" "}
            {medication.status === MedicationStatus.Active && (
                <Tag label={getText("ActiveLabel")} variant="success" size="sm" theme="light" />
            )}
            {medication.status === MedicationStatus.Expired && (
                <Tag label={getText("ExpiredLabel")} variant="error" size="sm" theme="light" />
            )}
            {medication.status === MedicationStatus.Suspended && (
                <Tag label={getText("SuspendedLabel")} variant="neutral" size="sm" theme="light" />
            )}
        </div>
    );
}

/**
 * MedicationTable displays a list of active medications for a given patient in a table format.
 * This version uses a native HTML table and dynamically generates the columns.
 */
function MedicationTable({ medications }: Readonly<MedicationTableProps>): JSX.Element {
    const { getText } = useTranslation();

    const columns = [
        {
            headerText: getText("MedicationLabel"),
            accessor: "name",
            cellRenderer: MedicationNameCellRenderer
        },
        {
            headerText: getText("StartDateLabel"),
            accessor: "startDate" },
        {
            headerText: getText("EndDateDLabel"),
            accessor: "endDate"

        },
        {
            headerText: getText("DosageLabel"),
            accessor: "dosage"
        },
        {
            headerText: getText("QuantityLabel"),
            accessor: "quantity"
        },
        {
            headerText: getText("RenewalsLabel"),
            accessor: "renewals"
        },
        {
            headerText: getText("DurationLabel"),
            accessor: "duration"
        }
    ];

    return (
        <SimpleTable
            keyPrefix="MedicationTableTable"
            data={medications}
            columns={columns}
            getId={(item: MedicationTableItem): string => item.id}
        />
    );
}

function MedicationNameCellRenderer({ item }: Readonly<{ item: MedicationTableItem }>): JSX.Element {
    return <NameCell {...item} />;
}

export default MedicationTable;

export {
    MedicationStatus,
    MedicationTableItem,
    MedicationTableProps
};
